
<script>
    import {PATH_PREFIX} from '../../../apiUrls.js';
    import {onMount} from 'svelte';
    import DataTable from '../../DataGrid/DataTable.svelte';
    import HyperlinkColumn from '../../DataGrid/ColumnRenderers/HyperlinkColumn.svelte';
    import DocumentNumberRenderer from './DocumentNumberRenderer.svelte';
    import {sgettext} from '../../../i18n.js';
    import {get, submit} from '../../../utils.js';
    import Button from '../../Button.svelte';
    import ConfirmationModal from '../../ConfirmationModal.svelte';
    import Modal from '../../Modal.svelte';
    import TextInput from '../../TextInput.svelte';
    import ComboSelect from '../../ComboSelect/ComboSelect.svelte';
    import yup, {array, object, number, string} from 'yup';
    import {validateForm} from '../../Form/utils.js';
    import Errors from '../../Errors.svelte';

    export let activityId = 0;
    export let activityVersionId = 0;
    export let listUrl = `${PATH_PREFIX}/sw/instruction/instructions_pdf_metadata/`;
    let instructionsTemplateOptionsUrl = `${PATH_PREFIX}/sw/v2/api/activity_instructions_pdf_templates/`;  // {% url 'activity-instructions-pdf-templates-list' pk %}
    let activityTemplateOptionsUrl = `${PATH_PREFIX}/sw/v2/api/activity_version/templates-for-instructions-generation/`;  // {% url 'activity-templates-for-instructions-generation-list' pk %}

    let allRows = [];
    let rows = [];  // same as allRows but filtered by active/expired for display in the data table
    let errors = null;

    let sendingExpire = false;
    let instructionsPDFMetadataToExpire = null;
    let instructionsPDFMetadataExpirationModal = 'instructionsPDFMetadataExpirationModal';

    export let currentActivityTemplate = null;
    export let canConfigure = false;

    let instructionsTemplateOptions = [];
    let activityTemplateOptions = [];
    let selectedActivityTemplate = null;  // pdf generation will be based off of the selected activity template
    let selectedInstructionsPDFTemplate = null;
    let selectedVersionNumber = null;
    let sendingGeneration = false;
    let instructionsPDFMetadataGenerationModal = 'instructionsPDFMetadataGenerationModal';
    let instructionsPDFMetadataSuccessModal = 'instructionsPDFMetadataSuccessModal';

    let total;
    let currentPage = 1;
    let perPage = 10;

    $: total = allRows.filter(r => r.expired === !activeShowing).length;

    let columns = {
        document_number: {
            label: sgettext('$$DOCUMENT$$'),
            style: 'padding-top: 1rem',
            renderer: DocumentNumberRenderer,
        },
        document_version: {
            label: sgettext('$$DOCUMENT_VERSION$$'),
            style: 'padding-top: 1rem',
        },
        activity_version: {
            label: sgettext('$$ACTIVITY_TEMPLATE$$'),
            renderer: HyperlinkColumn,
            props: {
                getHyperlink: row => `${PATH_PREFIX}/sw/builder/activityversion/${row.activity_version.id}/detail/`,
                getLinkText: row => row.activity_version.name,
                openInNewTab: true
            },
            style: 'padding-top: 1rem',
        },
        template: {
            label: sgettext('$$ACTIVITY_TEMPLATE_VERSION$$'),
            renderer: HyperlinkColumn,
            props: {
                getHyperlink: row => `${PATH_PREFIX}/sw/builder/activityversion/${row.activity_version.id}/detail/`,
                getLinkText: row => `${row.activity_version.version_number} (${row.activity_version.template_status})`,
                openInNewTab: true
            },
            style: 'padding-top: 1rem',
        }
    }

    let rowActions = {
        download: {
            key: 'download',
            width: '100px',
            label: sgettext("$$DOWNLOAD$$"),
            btnClass: 'outline',
            handler: (event, item) => {
                if (item.expired) {
                    alert(sgettext('$$DOCUMENT_EXPIRED_MAY_LONGER_DOWNLOADED$$'));
                }
                else {
                    window.open(item.location);
                }
            },
            disabled: (item) => item.expired
        },
    }

    if (canConfigure) {
      rowActions['remove'] = {
        label: sgettext("$$EXPIRE$$"),
        btnClass: 'outline',
        handler: (event, item) => {
          instructionsPDFMetadataToExpire = item;
          jQuery(`#${instructionsPDFMetadataExpirationModal}`).modal('show');
        },
        disabled: (item) => item.expired
      }
    }

    let schema = object().shape({
        activity_template: number()
            .required(sgettext('$$REQUIRED_FIELD$$'))
            .typeError(sgettext('$$REQUIRED_FIELD$$')),
        instructions_pdf_template_id: number()
            .required(sgettext('$$REQUIRED_FIELD$$'))
            .typeError(sgettext('$$REQUIRED_FIELD$$')),
        document_version: string()
            .test('len', sgettext('$$CANNOT_EXCEED_32_CHARACTERS$$'), val => val.length <= 32)
            .required(sgettext('$$REQUIRED_FIELD$$'))
            .typeError(sgettext('$$REQUIRED_FIELD$$')),
    });

    function getDefaultFieldIsValid() {
        return {
            activity_template: {
                isValid: true,
                errors: '',
            },
            instructions_pdf_template_id: {
                isValid: true,
                errors: '',
            },
            document_version: {
                isValid: true,
                errors: '',
            },
        };
    }

    let fieldIsValid = getDefaultFieldIsValid();

    async function handleRemove() {
        if (!sendingExpire) {
            sendingExpire = true;

            const url = `${listUrl}${instructionsPDFMetadataToExpire.id}/`;
            const payload = {...instructionsPDFMetadataToExpire};
            payload.expired = true;

            await submit(url, 'PUT', payload);

            let foundIndex = allRows.findIndex(x => x.id === instructionsPDFMetadataToExpire.id);
            allRows[foundIndex].expired = true;
            showActive();
            jQuery(`#${instructionsPDFMetadataExpirationModal}`).modal('hide');
            sendingExpire = false;
        }
    }

    async function handleCancelRemove() {
        jQuery(`#${instructionsPDFMetadataExpirationModal}`).modal('hide');
    }

    function resetFormValidation() {
        fieldIsValid = getDefaultFieldIsValid();
    }

    function resetGenerationForm() {
        selectedActivityTemplate = activityTemplateOptions.length > 0 ? activityTemplateOptions[0].id : null;
        selectedInstructionsPDFTemplate = instructionsTemplateOptions.length > 0 ? instructionsTemplateOptions[0].id : null ;
        selectedVersionNumber = null;

        resetFormValidation();
    }

    async function onGenerateInstructions() {
        await getInstructionsTemplateOptions();
        jQuery(`#${instructionsPDFMetadataGenerationModal}`).modal('show');
    }

    async function getInstructionsTemplateOptions() {
        const url = `${instructionsTemplateOptionsUrl}${activityId}/`
        const response = await get(url);
        let json = await response.json();
        let available = json['activity_instructions_pdf_templates'];
        instructionsTemplateOptions = [...available.map(x => ({name: x.document_number, id: x.id}))];

        if (instructionsTemplateOptions.length === 1) {
            selectedInstructionsPDFTemplate = instructionsTemplateOptions[0].id;
        }
    }

    async function getActivityTemplateOptions() {
        const url = `${activityTemplateOptionsUrl}?activity=${activityId}`
        const response = await get(url);
        let json = await response.json();
        let available = json['results'];
        activityTemplateOptions = [...available.map(x => ({id: x.id, name: `${x.template_status} v${x.version_number} - ${x.name}`}))];
        if (activityTemplateOptions.length > 0) {
            selectedActivityTemplate = activityTemplateOptions[0].id;
        }
    }

    async function handleCancelGeneration() {
        jQuery(`#${instructionsPDFMetadataGenerationModal}`).modal('hide');
        resetGenerationForm();
    }

    async function handleGenerationSubmit() {
        try {
            if (!sendingGeneration) {
                sendingGeneration = true;

                const url = `${PATH_PREFIX}/sw/instruction/instructions-export-trigger/${selectedActivityTemplate}/`;
                const payload = {
                    activity_template: selectedActivityTemplate,
                    instructions_pdf_template_id: selectedInstructionsPDFTemplate,
                    document_version: selectedVersionNumber
                };

                await validateForm(schema, payload);

                let response = await submit(url, 'POST', payload);

                if (response.ok) {
                    await handleCancelGeneration();
                    jQuery(`#${instructionsPDFMetadataSuccessModal}`).modal('show');
                    resetGenerationForm();
                }
                else {
                    let err = await response.json();
                    errors = err;
                }

                sendingGeneration = false;
            }
        } catch (err) {
            sendingGeneration = false;
            resetFormValidation();
            err.inner.forEach(er => {
                fieldIsValid[er.path].isValid = false;
                fieldIsValid[er.path].errors = er.message;
            });
        }

    }

    async function getRows() {
        const response = await get(`${listUrl}?activity_version__activity=${activityId}`);
        let json = await response.json();
        allRows = json['results'];

        await showActive();
        changePage({page: 1});
    }

    let activeShowing = true;
    async function showActive() {
        activeShowing = true;
        rows = allRows.filter(r => !r.expired)
        changePage({page: 1});
    }

    async function showExpired() {
        activeShowing = false;
        rows = allRows.filter(r => r.expired)
        changePage({page: 1});
    }

    function changePage(params) {
        currentPage = params.page;
        rows = allRows.filter(r => r.expired === !activeShowing).slice((currentPage - 1) * perPage, currentPage * perPage);
    }

    function openConfig() {
        window.open(`${PATH_PREFIX}/sw/builder/${activityId}/edit/`, "_blank");
    }

    onMount(async () => {
        getInstructionsTemplateOptions();
        getActivityTemplateOptions();
        getRows();
    });
</script>

<ConfirmationModal
    id={instructionsPDFMetadataExpirationModal}
    title="{sgettext('$$EXPIRE_INSTRUCTIONS_PDF$$')}"
    onYes={handleRemove}
    onNo={handleCancelRemove}
    data={instructionsPDFMetadataToExpire}
    sending={sendingExpire}>

    <div slot="message">
        {#if instructionsPDFMetadataToExpire}
            <p>{sgettext('$$SURE_WANT_EXPIRE_PDF$$')}</p>
<!--            <p>{sgettext('$$DOCUMENT_NUMBER$$')}: {instructionsPDFMetadataToExpire.document_number}</p>-->
<!--            <p>{sgettext('$$VERSION$$')}: {instructionsPDFMetadataToExpire.document_version}</p>-->
<!--            <p>{sgettext('$$ACTIVITY_TEMPLATE$$')}: {instructionsPDFMetadataToExpire.activity_version.name}</p>-->
<!--            <p>{sgettext('$$ACTIVITY_TEMPLATE_VERSION$$')}: {instructionsPDFMetadataToExpire.activity_version.version_number}</p>-->
        {/if}
    </div>
</ConfirmationModal>


<Modal
    id={instructionsPDFMetadataGenerationModal}
    title={sgettext("$$GENERATE_INSTRUCTIONS$$")}
    backdrop={'static'}>
    <div slot="body">
        <slot name="message">
            <p>
                {sgettext("$$INSTRUCTIONS_GENERATED_ACTIVITY_TEMPLATE$$")}.
            </p>

            <p>
                {#if instructionsTemplateOptions.length > 0}
                    {sgettext("$$PLEASE_SELECT_DOCUMENT_NUMBER_ENTER$$")}:
                {:else}
                    <span class="text-danger">
                        <i class="fa fa-warning"></i>
                        {sgettext("$$INSTRUCTIONS_NEED_CONFIGURED_ACTIVITY$$")}
                    </span>
                    {#if canConfigure}
                        <a on:click={openConfig} href="#">{sgettext("$$CONFIGURE$$")}</a>
                    {/if}
                {/if}
            </p>

            <p>
                {#if activityTemplateOptions.length === 0 }
                    <span class="text-danger">
                        <i class="fa fa-warning"></i>
                        {sgettext("$$INSTRUCTIONS_GENERATED_CURRENT_VALID$$")}
                    </span>
                {/if}
            </p>

            <div class="mb-1">
                <ComboSelect
                    id="selectedActivityTemplate"
                    label={sgettext("$$ACTIVITY_TEMPLATE$$")}
                    options={activityTemplateOptions}
                    bind:value={selectedActivityTemplate}
                    bind:isValid={fieldIsValid.activity_template.isValid}
                    bind:errors={fieldIsValid.activity_template.errors}
                    required="true"/>

            </div>
            <div class="mb-1">
                <ComboSelect
                    id="selectedInstructionsPDFTemplate"
                    label={sgettext("$$DOCUMENT_NUMBER$$")}
                    options={instructionsTemplateOptions}
                    bind:value={selectedInstructionsPDFTemplate}
                    bind:isValid={fieldIsValid.instructions_pdf_template_id.isValid}
                    bind:errors={fieldIsValid.instructions_pdf_template_id.errors}
                    required="true"/>

            </div>

            <div class="mb-1">
                <TextInput
                    id="versionNumber"
                    label={sgettext("$$VERSION_NUMBER$$")}
                    bind:value={selectedVersionNumber}
                    bind:isValid={fieldIsValid.document_version.isValid}
                    bind:errors={fieldIsValid.document_version.errors}
                    schema={schema}
                />
            </div>

            <Errors {errors}/>

        </slot>
    </div>

    <div class="footer" slot="footer">
        <Button
            type="outline"
            on:click={handleCancelGeneration}
            name={sgettext("$$CANCEL$$")} />
        <Button
            type="primary"
            on:click={() => handleGenerationSubmit()}
            name={sgettext("$$GENERATE$$")}
            sending={sendingGeneration} />
    </div>
</Modal>

<Modal
    id={instructionsPDFMetadataSuccessModal}
    title={sgettext("$$GENERATE_INSTRUCTIONS$$")}
    backdrop={'static'}>
    <div slot="body">
        <slot name="message">
            <p>
                {sgettext("$$GENERATION_PROGRESS_NOTIFIED_PROCESS$$")}.
            </p>
        </slot>
    </div>

    <div class="footer" slot="footer">
        <Button
            type="primary"
            on:click={() => jQuery(`#${instructionsPDFMetadataSuccessModal}`).modal('hide')}
            name={sgettext("$$OK_1$$")} />
    </div>
</Modal>

<div class="row mb-1">
    <div class="col-6">
        <h3>{sgettext("$$INSTRUCTIONS$$")}</h3>
    </div>
</div>
<div class="row mb-1">
    <div class="col-6">
        <Button
                name={sgettext("$$ACTIVE$$")}
                type={activeShowing? "primary" : "outline"}
                on:click={showActive}
        />
        <Button
                name={sgettext("$$EXPIRED$$")}
                type={activeShowing? "outline" : "primary"}
                on:click={showExpired}
        />
    </div>
    <div class="col-6 text-right">
        <Button
                name={sgettext("$$GENERATE_INSTRUCTIONS$$")}
                type="primary"
                on:click={onGenerateInstructions}>
            <div slot="text">
                <i class="fas fa-plus"></i> {sgettext("$$GENERATE_INSTRUCTIONS$$")}
            </div>
        </Button>
    </div>
</div>

<DataTable
    small={true}
    {columns}
    {rows}
    {rowActions}
    showFooter={total > perPage}
    {total}
    {perPage}
    {currentPage}
    {changePage}
    footerColSpan={5}
/>

<br/>
